import * as THREE from "three";
import type { Collectable, CollectableKind } from "../collectables";
import { pickupTexture } from "./textures";

// The run's pickups as floating, spinning badges: yellow S-K-A-T-E letters,
// pink C-O-M-B-O letters and a blue Secret Tape. Each badge is two planes
// back to back so the label reads the right way round from either side.

const SIZE = 1.1;
const STYLE: Record<CollectableKind, { bg: string; fg: string; label?: string }> = {
  skate: { bg: "#ffd166", fg: "#ffffff" },
  combo: { bg: "#ef476f", fg: "#ffffff" },
  tape: { bg: "#118ab2", fg: "#ffffff", label: "TAPE" },
};

export class PickupMeshes {
  readonly root = new THREE.Group();
  private items: { c: Collectable; mesh: THREE.Group }[] = [];
  private t = 0;

  constructor(pickups: readonly Collectable[]) {
    const geo = new THREE.PlaneGeometry(SIZE, SIZE);
    const mats = new Map<string, THREE.MeshBasicMaterial>();
    for (const c of pickups) {
      const style = STYLE[c.kind];
      const label = style.label ?? c.letter;
      const key = `${c.kind}:${label}`;
      let mat = mats.get(key);
      if (!mat) {
        const map = pickupTexture(label, style.bg, style.fg);
        mat = new THREE.MeshBasicMaterial({ map, color: map ? 0xffffff : style.bg, transparent: true, alphaTest: 0.5, toneMapped: false });
        mats.set(key, mat);
      }
      const mesh = new THREE.Group();
      const front = new THREE.Mesh(geo, mat);
      const back = new THREE.Mesh(geo, mat);
      back.rotation.y = Math.PI;
      mesh.add(front, back);
      mesh.position.set(c.x, c.y, c.z);
      this.root.add(mesh);
      this.items.push({ c, mesh });
    }
  }

  /** Spins and bobs the badges; hides the ones `shown` says are gone. */
  update(dt: number, shown: (c: Collectable) => boolean): void {
    this.t += dt;
    for (const [i, { c, mesh }] of this.items.entries()) {
      mesh.visible = shown(c);
      mesh.rotation.y = this.t * 2.2 + i;
      mesh.position.y = c.y + Math.sin(this.t * 2.5 + i) * 0.08;
    }
  }

  dispose(): void {
    const done = new Set<THREE.Material | THREE.BufferGeometry>();
    this.root.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      for (const r of [o.geometry, o.material as THREE.MeshBasicMaterial]) {
        if (done.has(r)) continue;
        done.add(r);
        if (r instanceof THREE.MeshBasicMaterial) r.map?.dispose();
        r.dispose();
      }
    });
  }
}
